import { type QueryClient, type QueryKey } from '@tanstack/react-query';
import type {
  ExcludedAlertState,
  GroupedAlert,
  MutedAlertState,
} from '@kirin/api-client/contracts/alerts';
import { queryKeys } from './queryKeys';

/**
 * Shared cache helpers for `useAlertMutes` and `useAlertExclusions`.
 *
 * Both hooks share the same optimistic write path (cancel → patch lists
 * + occurrences → snapshot → invalidate on settle); centralizing it
 * here keeps bug fixes single-sourced. Private to those two hooks.
 */

/** `organization` requires prior authz; `account` is self-scoped. */
export type AlertSuppressionScope =
  | { kind: 'organization'; organizationId: string }
  | { kind: 'account' };

/** Snapshot captured by `onMutate` for `onError` rollback. */
export type CacheSnapshot = ReadonlyArray<readonly [QueryKey, unknown]>;

/** Either response shape for grouped-alerts list endpoints. */
export interface GroupedAlertsListLike {
  data?: GroupedAlert[];
  alerts?: GroupedAlert[];
}

/** Alert-occurrences detail response (only the parent alert is patched). */
export interface AlertOccurrencesLike {
  alert?: GroupedAlert;
}

/** Discriminated patch: fragment key narrows the value type per branch. */
export type SuppressionPatch =
  | { fragment: 'mute'; value: MutedAlertState }
  | { fragment: 'exclude'; value: ExcludedAlertState };

/**
 * Predicate matching every grouped-alerts list cache entry in scope
 * (org: any variant for `organizationId`; account: list/listFiltered).
 */
export function listMatcher(
  scope: AlertSuppressionScope
): (query: { queryKey: QueryKey }) => boolean {
  return (query: { queryKey: QueryKey }): boolean => {
    const [head, kind, params] = query.queryKey as [
      string?,
      string?,
      { organizationId?: string }?,
    ];
    if (scope.kind === 'organization') {
      return (
        head === 'organizations' &&
        kind === 'groupedAlerts' &&
        !!params &&
        params.organizationId === scope.organizationId
      );
    }
    return head === 'alerts' && (kind === 'list' || kind === 'listFiltered');
  };
}

/** Predicate matching alert-occurrences for `(scope, alertKey)`. */
export function occurrencesMatcher(
  scope: AlertSuppressionScope,
  alertKey: string
): (query: { queryKey: QueryKey }) => boolean {
  return (query: { queryKey: QueryKey }): boolean => {
    const key = query.queryKey;
    if (scope.kind === 'organization') {
      const [head, kind, params] = key as [
        string?,
        string?,
        { organizationId?: string; alertKey?: string }?,
      ];
      return (
        head === 'organizations' &&
        kind === 'alertOccurrences' &&
        !!params &&
        params.organizationId === scope.organizationId &&
        params.alertKey === alertKey
      );
    }
    const [head, kind, keyAlertKey] = key as [string?, string?, string?];
    return (
      head === 'alerts' && kind === 'occurrences' && keyAlertKey === alertKey
    );
  };
}

/** Apply a suppression patch to a single GroupedAlert row. */
function applyPatchToAlert(
  alert: GroupedAlert,
  patch: SuppressionPatch
): GroupedAlert {
  if (patch.fragment === 'mute') {
    return { ...alert, mute: patch.value };
  }
  return { ...alert, exclude: patch.value };
}

/**
 * Patch the alert in a grouped-alerts list, preserving response shape
 * (`data` for table-engine, `alerts` for legacy) and other fragments.
 * Returns same reference when no row matches (avoids spurious renders).
 */
function patchListEntry(
  cached: GroupedAlertsListLike | undefined,
  alertKey: string,
  patch: SuppressionPatch
): GroupedAlertsListLike | undefined {
  if (!cached) return cached;
  if (Array.isArray(cached.data)) {
    let touched = false;
    const updated = cached.data.map(item => {
      if (item.alertKey !== alertKey) return item;
      touched = true;
      return applyPatchToAlert(item, patch);
    });
    return touched ? { ...cached, data: updated } : cached;
  }
  if (Array.isArray(cached.alerts)) {
    let touched = false;
    const updated = cached.alerts.map(item => {
      if (item.alertKey !== alertKey) return item;
      touched = true;
      return applyPatchToAlert(item, patch);
    });
    return touched ? { ...cached, alerts: updated } : cached;
  }
  return cached;
}

/** Patch the parent `alert.<fragment>` in an occurrences entry. */
function patchOccurrencesEntry(
  cached: AlertOccurrencesLike | undefined,
  alertKey: string,
  patch: SuppressionPatch
): AlertOccurrencesLike | undefined {
  if (!cached?.alert) return cached;
  if (cached.alert.alertKey !== alertKey) return cached;
  return { ...cached, alert: applyPatchToAlert(cached.alert, patch) };
}

/**
 * Cancel in-flight list/occurrences queries before patching. Otherwise
 * a refetch in flight can resolve AFTER `setQueryData` and clobber the
 * optimistic write with stale data (visual flicker until invalidation).
 */
export async function cancelOverlappingQueries(
  queryClient: QueryClient,
  scope: AlertSuppressionScope,
  alertKey: string
): Promise<void> {
  await Promise.all([
    queryClient.cancelQueries({ predicate: listMatcher(scope) }),
    queryClient.cancelQueries({
      predicate: occurrencesMatcher(scope, alertKey),
    }),
  ]);
}

/**
 * Patch every in-scope list + occurrences entry; returns rollback
 * snapshot. Never touches the OTHER fragment on the same row (mute and
 * exclude are orthogonal).
 */
export function applyOptimisticPatch(
  queryClient: QueryClient,
  scope: AlertSuppressionScope,
  alertKey: string,
  patch: SuppressionPatch
): CacheSnapshot {
  const snapshots: Array<[QueryKey, unknown]> = [];

  for (const [key, data] of queryClient.getQueriesData<GroupedAlertsListLike>({
    predicate: listMatcher(scope),
  })) {
    snapshots.push([key, data]);
    queryClient.setQueryData<GroupedAlertsListLike | undefined>(key, current =>
      patchListEntry(current, alertKey, patch)
    );
  }

  for (const [key, data] of queryClient.getQueriesData<AlertOccurrencesLike>({
    predicate: occurrencesMatcher(scope, alertKey),
  })) {
    snapshots.push([key, data]);
    queryClient.setQueryData<AlertOccurrencesLike | undefined>(key, current =>
      patchOccurrencesEntry(current, alertKey, patch)
    );
  }

  return snapshots;
}

/** Restore the exact set captured by {@link applyOptimisticPatch}. */
export function restoreSnapshot(
  queryClient: QueryClient,
  snapshot: CacheSnapshot
): void {
  for (const [key, data] of snapshot) {
    queryClient.setQueryData(key, data);
  }
}

/**
 * Predicate matching the three org-scope caches that became
 * exclusion-aware in KIR-2363:
 *  1. `dashboardTopAlerts(orgId, …)` — TopAlerts widget.
 *  2. `dashboardAlerts(orgId, …)` — alerts-over-time chart.
 *  3. TopAlerts drill-down: `['scans', 'organization', orgId, 'users',
 *     alertName, from, to]` (hand-rolled in `useScans.ts`; update both
 *     sites if that key migrates into `queryKeys.ts`).
 */
function exclusionAwareOrgWidgetMatcher(
  organizationId: string
): (query: { queryKey: QueryKey }) => boolean {
  return (query: { queryKey: QueryKey }): boolean => {
    const key = query.queryKey;

    const [orgHead, orgKind, orgParams] = key as [
      string?,
      string?,
      { organizationId?: string }?,
    ];
    if (
      orgHead === 'organizations' &&
      (orgKind === 'dashboardTopAlerts' || orgKind === 'dashboardAlerts') &&
      !!orgParams &&
      orgParams.organizationId === organizationId
    ) {
      return true;
    }

    const [scansHead, scansSection, scansOrgId, scansLeaf] = key as [
      string?,
      string?,
      string?,
      string?,
    ];
    if (
      scansHead === 'scans' &&
      scansSection === 'organization' &&
      scansOrgId === organizationId &&
      scansLeaf === 'users'
    ) {
      return true;
    }

    return false;
  };
}

/**
 * Invalidate everything that may have read suppression state in scope:
 * lists + occurrences always, plus (org-scope) USP, plus (org+exclude)
 * the exclusion-aware dashboard widgets and TopAlerts drill-down.
 *
 * Account scope skips USP + dashboards (org-only surfaces today). Mute
 * skips dashboards by design — KIR-2363 keeps muted alerts in rollups,
 * so a mute/unmute does NOT touch `dashboardTopAlerts` /
 * `dashboardAlerts`. USP runs for both fragments (filters mute+exclude).
 */
export function invalidateAffectedQueries(
  queryClient: QueryClient,
  scope: AlertSuppressionScope,
  alertKey: string,
  fragment: SuppressionPatch['fragment']
): void {
  void queryClient.invalidateQueries({ predicate: listMatcher(scope) });
  void queryClient.invalidateQueries({
    predicate: occurrencesMatcher(scope, alertKey),
  });
  // A single-row action can change a row that a live bulk selection still
  // counts — drop the bulk eligibility preview so the bar re-derives its
  // labels/counts (KIR-3252). Scope-aware, so it applies to BOTH the org and
  // personal Alerts screens (an open personal bulk bar would otherwise show
  // stale counts after a single-row mute/exclude).
  invalidateBulkEligibility(queryClient, scope);
  // USP + the exclusion-aware dashboard widgets are org-only surfaces today.
  if (scope.kind === 'organization') {
    void queryClient.invalidateQueries({
      queryKey: queryKeys.userSecurityPosture.all,
    });
    if (fragment === 'exclude') {
      void queryClient.invalidateQueries({
        predicate: exclusionAwareOrgWidgetMatcher(scope.organizationId),
      });
    }
  }
}

/**
 * Drop every bulk-eligibility cache entry for an org so the bulk action bar
 * re-derives its action labels/counts from fresh server state. Shared by the
 * single-alert and bulk invalidation paths (KIR-3252).
 */
function invalidateBulkEligibility(
  queryClient: QueryClient,
  scope: AlertSuppressionScope
): void {
  void queryClient.invalidateQueries({
    predicate: (query: { queryKey: QueryKey }): boolean => {
      if (scope.kind === 'organization') {
        const [head, kind, params] = query.queryKey as [
          string?,
          string?,
          { organizationId?: string }?,
        ];
        return (
          head === 'organizations' &&
          kind === 'bulkAlertEligibility' &&
          !!params &&
          params.organizationId === scope.organizationId
        );
      }
      const [head, kind] = query.queryKey as [string?, string?];
      return head === 'alerts' && kind === 'bulkAlertEligibility';
    },
  });
}

/**
 * Invalidate everything a bulk alert action (KIR-3252) may have changed in an
 * organization. Unlike {@link invalidateAffectedQueries}, this is NOT keyed on a
 * single `alertKey` — a bulk write touches many keys (and in `query` mode the
 * affected keys aren't known client-side), so it invalidates the whole
 * grouped-alerts list set, ALL org alert-occurrences entries, USP, the bulk
 * eligibility preview (so the bar re-derives action labels from new states),
 * and — for exclude/remove — the exclusion-aware dashboard widgets.
 */
export function invalidateBulkAffectedQueries(
  queryClient: QueryClient,
  scope: AlertSuppressionScope,
  fragment: SuppressionPatch['fragment']
): void {
  void queryClient.invalidateQueries({ predicate: listMatcher(scope) });

  // Every alert-occurrences entry in scope (bulk can change any key).
  void queryClient.invalidateQueries({
    predicate: (query: { queryKey: QueryKey }): boolean => {
      if (scope.kind === 'organization') {
        const [head, kind, params] = query.queryKey as [
          string?,
          string?,
          { organizationId?: string }?,
        ];
        return (
          head === 'organizations' &&
          kind === 'alertOccurrences' &&
          !!params &&
          params.organizationId === scope.organizationId
        );
      }
      const [head, kind] = query.queryKey as [string?, string?];
      return head === 'alerts' && kind === 'occurrences';
    },
  });

  // Re-derive the bulk action bar: drop every bulk-eligibility entry in scope.
  invalidateBulkEligibility(queryClient, scope);

  // USP + the exclusion-aware dashboard widgets are org-only surfaces today;
  // personal scope has neither, so it skips both (mirrors the single-alert path).
  if (scope.kind === 'organization') {
    void queryClient.invalidateQueries({
      queryKey: queryKeys.userSecurityPosture.all,
    });

    if (fragment === 'exclude') {
      void queryClient.invalidateQueries({
        predicate: exclusionAwareOrgWidgetMatcher(scope.organizationId),
      });
    }
  }
}
